import React from 'react';
import { usePermissions } from '../hooks/usePermissions';

/**
 * Componente para mostrar/ocultar elementos basado en permisos
 */
export const PermissionGuard = ({ 
  children, 
  permission, 
  roles, 
  fallback = null,
  inverse = false 
}) => {
  const { hasPermission, hasAnyRole } = usePermissions();

  let hasAccess = true;

  // Verificar por permiso específico
  if (permission) {
    hasAccess = hasPermission(permission);
  }

  // Verificar por roles
  if (roles && hasAccess) {
    hasAccess = hasAnyRole(roles);
  }

  // Si inverse es true, invertir la lógica
  if (inverse) {
    hasAccess = !hasAccess;
  }

  return hasAccess ? children : fallback;
};

/**
 * Componente específico para elementos que solo administradores pueden ver
 */
export const AdminOnly = ({ children, fallback = null }) => {
  return (
    <PermissionGuard roles={[1]} fallback={fallback}>
      {children}
    </PermissionGuard>
  );
};

/**
 * Componente para elementos que administradores, administrativos y coordinadores pueden ver
 */
export const AdminAndStaff = ({ children, fallback = null }) => {
  return (
    <PermissionGuard roles={[1, 2, 4]} fallback={fallback}>
      {children}
    </PermissionGuard>
  );
};

/* componente para administradores y coordinadores */
export const AdminAndCoordinator = ({ children, fallback = null }) => {
  return (
    <PermissionGuard roles={[1, 4]} fallback={fallback}>
      {children}
    </PermissionGuard>
  );
};

/**
 * Componente para elementos que solo auditores NO pueden ver
 */
export const NotAuditor = ({ children, fallback = null }) => {
  return (
    <PermissionGuard roles={[3]} inverse={true} fallback={fallback}>
      {children}
    </PermissionGuard>
  );
};

/**
 * Componente para elementos de solo lectura (todos pueden ver)
 */
export const ReadOnly = ({ children, fallback = null }) => {
  return (
    <PermissionGuard roles={[1, 2, 3]} fallback={fallback}>
      {children}
    </PermissionGuard>
  );
};

/**
 * Hook para componentes condicionales basados en permisos
 */
export const useConditionalRender = () => {
  const permissions = usePermissions();

  const renderIf = (condition, component, fallback = null) => {
    return condition ? component : fallback;
  };

  const renderForAdmin = (component, fallback = null) => {
    return renderIf(permissions.isAdmin(), component, fallback);
  };

  const renderForAdminAndStaff = (component, fallback = null) => {
    return renderIf(permissions.canManageData(), component, fallback);
  };

  const renderForAuditor = (component, fallback = null) => {
    return renderIf(permissions.isAuditor(), component, fallback);
  };

  const renderForPermission = (permission, component, fallback = null) => {
    return renderIf(permissions.hasPermission(permission), component, fallback);
  };

  return {
    renderIf,
    renderForAdmin,
    renderForAdminAndStaff,
    renderForAuditor,
    renderForPermission,
    ...permissions
  };
};

export default PermissionGuard;
